/**
 * The KB article editor — Step 33 rider (Kai: "the backend looks crap
 * compared to the front").
 *
 * A DISTINCT editor from the ticket composer, and it has to be. The composer
 * runs StarterKit with headings off, no images and no colour, because a reply
 * is not an article. Pointing that editor at an article was actively
 * destructive: opening an imported article and pressing Save silently threw
 * away its headings, images, fonts and brand colours — the exact content the
 * signature templates exist for.
 *
 * So this editor supports what articles actually contain, and — because
 * Tiptap can only ever round-trip what its schema knows — it CHECKS before
 * you type whether anything would be lost, and says so instead of quietly
 * flattening the article on save.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading2,
  Heading3,
  ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  TriangleAlert,
  Underline as UnderlineIcon,
} from 'lucide-react';
import { uploadFile } from './api';
import { cn } from '../../lib/utils';
import { usePrompt } from '../../lib/confirm';

/** Things the editor's schema cannot represent. Detected on the ORIGINAL
 *  HTML so the warning appears before anyone types, not after they save. */
function losses(html: string): string[] {
  const out: string[] = [];
  if (/<table\b/i.test(html)) out.push('tables');
  if (/font-family\s*:/i.test(html)) out.push('custom fonts');
  if (/font-size\s*:/i.test(html)) out.push('font sizes');
  if (/<(h[1-6])\b[^>]*style=/i.test(html)) out.push('styled headings');
  return out;
}

const TOOLBAR_BTN =
  'rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Tb({
  onClick,
  active,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(TOOLBAR_BTN, active && 'bg-accent text-foreground')}
    >
      {children}
    </button>
  );
}

/** The palette offered for text colour. Deliberately small: an article is
 *  not a design tool, and an arbitrary colour picker produces the
 *  unreadable-on-one-theme colours articleTheme.ts then has to rescue. */
const COLOURS = ['', '#C82613', '#B45309', '#047857', '#1D4ED8', '#6D28D9', '#6B7280'];

export function ArticleEditor({
  value,
  onChange,
  placeholder,
  articleId,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Images are attached to the article as they are uploaded, so they inherit
   *  its folder's visibility straight away rather than sitting unowned. */
  articleId?: string | undefined;
}) {
  const [original] = useState(value);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const willLose = useMemo(() => losses(original), [original]);
  const [dismissed, setDismissed] = useState(false);

  const editor = useEditor({
    extensions: [
      // Headings ON — the reference's own articles are structured with
      // "Part 1 —" / "Step 3" headings, and an article without them is a wall.
      StarterKit,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder ?? 'Write the article…' }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: 'kb-article focus:outline-none min-h-[24rem] px-4 py-3',
      },
    },
  });

  // Keep the editor in step when the article is swapped underneath it
  // (navigating between articles reuses this component).
  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value, false);
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div className="space-y-2">
      {willLose.length > 0 && !dismissed && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              This article uses formatting the editor cannot fully keep ({willLose.join(', ')}).
            </p>
            <p className="mt-0.5">
              It was imported from Freshdesk. Saving here will simplify it — the words, headings, links,
              images and colours are kept, the rest is not. Leave without saving if you would rather not.
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="shrink-0 underline underline-offset-2 hover:no-underline"
          >
            Got it
          </button>
        </div>
      )}

      {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}

      <div className="rounded-md border focus-within:ring-2 focus-within:ring-ring">
        <Toolbar editor={editor} articleId={articleId} onUploadError={(m) => setUploadError(m)} />
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({
  editor,
  articleId,
  onUploadError,
}: {
  editor: Editor;
  // `exactOptionalPropertyTypes` is on, so an optional prop that may actually
  // receive `undefined` has to say so.
  articleId?: string | undefined;
  onUploadError: (message: string | null) => void;
}) {
  const prompt = usePrompt();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5">
      <Tb
        label="Heading"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </Tb>
      <Tb
        label="Subheading"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </Tb>
      <span className="mx-1 h-4 w-px bg-border" />
      <Tb
        label="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </Tb>
      <Tb
        label="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </Tb>
      <Tb
        label="Underline"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-4 w-4" />
      </Tb>
      <span className="mx-1 h-4 w-px bg-border" />
      <Tb
        label="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </Tb>
      <Tb
        label="Numbered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </Tb>
      <Tb
        label="Quote"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="h-4 w-4" />
      </Tb>
      <Tb label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" />
      </Tb>
      <span className="mx-1 h-4 w-px bg-border" />
      <Tb
        label="Align left"
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft className="h-4 w-4" />
      </Tb>
      <Tb
        label="Align centre"
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter className="h-4 w-4" />
      </Tb>
      <Tb
        label="Align right"
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight className="h-4 w-4" />
      </Tb>
      <span className="mx-1 h-4 w-px bg-border" />
      <Tb
        label="Link"
        active={editor.isActive('link')}
        onClick={async () => {
          const prev = editor.getAttributes('link').href as string | undefined;
          const href = await prompt({
            title: 'Link',
            label: 'Address',
            initial: prev ?? 'https://',
            confirmLabel: 'Link it',
          });
          if (href === null) return;
          if (!href.trim()) {
            editor.chain().focus().unsetLink().run();
            return;
          }
          editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
        }}
      >
        <LinkIcon className="h-4 w-4" />
      </Tb>
      <Tb label={busy ? 'Uploading…' : 'Insert image'} onClick={() => fileInput.current?.click()}>
        <ImageIcon className={cn('h-4 w-4', busy && 'animate-pulse')} />
      </Tb>
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          // Reset immediately so picking the SAME file twice still fires.
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          onUploadError(null);
          const res = await uploadFile(
            file,
            articleId ? { ownerType: 'kb_article', ownerId: articleId } : undefined,
          );
          setBusy(false);
          if (!res.ok || !res.body) {
            onUploadError(res.error ?? 'Could not upload that image.');
            return;
          }
          editor.chain().focus().setImage({ src: res.body.url, alt: res.body.filename }).run();
        }}
      />
      <span className="mx-1 h-4 w-px bg-border" />
      <div className="flex items-center gap-1">
        {COLOURS.map((c) => (
          <button
            key={c || 'default'}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              c ? editor.chain().focus().setColor(c).run() : editor.chain().focus().unsetColor().run()
            }
            aria-label={c ? `Text colour ${c}` : 'Default text colour'}
            title={c ? `Colour ${c}` : 'Default colour'}
            className={cn(
              'h-4 w-4 rounded-full border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              !c && 'bg-transparent',
            )}
            style={c ? { backgroundColor: c } : undefined}
          />
        ))}
      </div>
    </div>
  );
}
